import { useRef, type DragEvent as ReactDragEvent } from 'react';
import { useIntl } from 'react-intl';

import { shouldClearFileDragging } from '../lib/deck-upload-drop.ts';
import {
  isUploadFailure,
  type DeckUploadItem,
  type MemberDeckPage,
} from '../lib/deck-page.ts';
import { SectionHeading } from './SectionHeading.tsx';
import { Button } from './ui/button.tsx';
import { Card, CardContent } from './ui/card.tsx';

export function DeckPageFiles({
  page,
  dragging = false,
  onFilesSelected = () => undefined,
  onDraggingChange = () => undefined,
  uploadItem = null,
}: {
  page: MemberDeckPage;
  dragging?: boolean;
  onFilesSelected?: (files: File[]) => void;
  onDraggingChange?: (dragging: boolean) => void;
  uploadItem?: DeckUploadItem | null;
}) {
  const intl = useIntl();
  const picker = useRef<HTMLInputElement>(null);
  const missingDocuments = page.member.documents.filter(
    (document) => !document.hasLocalCopy,
  );
  const failed = isUploadFailure(uploadItem?.messageId);
  const uploadMessage = uploadItem
    ? uploadItem.status === 'uploading'
      ? intl.formatMessage(
          { id: 'web.deckPage.uploading' },
          { name: uploadItem.name },
        )
      : intl.formatMessage(
          { id: uploadItem.messageId ?? 'web.deckPage.uploadFailed' },
          { name: uploadItem.name },
        )
    : null;
  const uploadFeedback = (
    <>
      <div className="m-0">{!failed ? uploadMessage : null}</div>
      <div className="m-0 text-destructive">
        {failed ? uploadMessage : null}
      </div>
    </>
  );

  const stopDrag = (event: ReactDragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <section
      className="relative"
      onDragEnter={(event) => {
        stopDrag(event);
        onDraggingChange(true);
      }}
      onDragLeave={(event) => {
        stopDrag(event);
        if (
          shouldClearFileDragging(
            event.relatedTarget,
            (target) =>
              target instanceof Node && event.currentTarget.contains(target),
          )
        )
          onDraggingChange(false);
      }}
      onDragOver={(event) => {
        stopDrag(event);
        onDraggingChange(true);
      }}
      onDrop={(event) => {
        stopDrag(event);
        onDraggingChange(false);
        onFilesSelected(Array.from(event.dataTransfer.files));
      }}
    >
      {dragging ? (
        <>
          <div className="pointer-events-none fixed inset-4 z-[2] rounded-[var(--radius)] border-[3px] border-dashed border-foreground bg-card/80" />
          <p className="pointer-events-none fixed inset-4 z-[3] grid place-items-center text-center text-2xl font-bold text-foreground">
            {intl.formatMessage({ id: 'web.deckPage.dropActive' })}
          </p>
        </>
      ) : null}
      <section className="grid gap-4">
        <SectionHeading
          description={
            missingDocuments.length > 0
              ? intl.formatMessage({ id: 'web.deckPage.uploadHint' })
              : undefined
          }
          title={intl.formatMessage({ id: 'web.deckPage.documents' })}
        />
        {missingDocuments.length > 0 ? (
          <>
            <input
              ref={picker}
              accept="application/pdf,.pdf"
              hidden
              multiple
              type="file"
              onChange={(event) => {
                const files = Array.from(event.currentTarget.files ?? []);
                event.currentTarget.value = '';
                onFilesSelected(files);
              }}
            />
            <Button
              className="min-h-11 w-fit font-semibold"
              variant="secondary"
              onClick={() => picker.current?.click()}
              type="button"
            >
              {intl.formatMessage({ id: 'web.deckPage.chooseFiles' })}
            </Button>
          </>
        ) : null}
        {uploadFeedback}
        {page.member.documents.length === 0 ? (
          <p className="text-muted-foreground">
            {intl.formatMessage({ id: 'web.decks.documentEmpty' })}
          </p>
        ) : (
          <ul className="m-0 grid list-none gap-3 p-0">
            {page.member.documents.map((document) => (
              <li
                id={`reference-document-${document.id}`}
                key={document.id}
                tabIndex={-1}
              >
                <Card className="border border-border bg-card">
                  <CardContent className="grid gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      <strong className="min-w-0 flex-1 break-words text-base font-semibold">
                        {document.title}
                      </strong>
                      <small className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                        {intl.formatMessage(
                          { id: 'web.decks.documentPages' },
                          { pageCount: document.expectedPageCount },
                        )}
                      </small>
                    </div>
                    <p className="m-0 text-sm text-muted-foreground">
                      {document.description}
                    </p>
                    <p className="m-0 text-sm text-muted-foreground">
                      {intl.formatMessage({
                        id: document.hasLocalCopy
                          ? 'web.deckPage.documentUploaded'
                          : 'web.deckPage.documentMissing',
                      })}
                    </p>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
